import * as React from 'react';

import { translate } from '~l10n/client';
import { Action } from '~shared/components/ActionMenu';
import { IconAnswerKey } from '~shared/components/icons/standard';
import { useWorkspaceActionPermissions } from '~teacher/hook/workspaces/settings';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

interface UseExportAnswerKeyActionParams {
  workspaceId: string;
  paywallCallback: () => void;
}

export function useExportAnswerKeyAction({
  paywallCallback,
  workspaceId,
}: UseExportAnswerKeyActionParams) {
  const { canExportAnswerKey, permissions } = useWorkspaceActionPermissions({
    workspaceId,
  });

  const [isAnswerKeyDialogOpen, setIsAnswerKeyDialogOpen] =
    React.useState(false);
  const [selectedQuizId, setSelectedQuizId] = React.useState<string | null>(
    null
  );
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<
    string | null
  >(null);

  const exportAnswerKeyAction = React.useMemo(
    () =>
      ({
        key: 'export-answer-key',
        title: translate('Export Answer Key'),
        icon: <IconAnswerKey />,
        action: ({ data, close }) => {
          const firstItem = data[0];
          if (firstItem.type === 'quiz') {
            const { id, workspaceId } = firstItem;
            setSelectedQuizId(id);
            setSelectedWorkspaceId(workspaceId);
            setIsAnswerKeyDialogOpen(true);
            close();
          }
        },
        constraint: ({ data: quizzes }: { data: WorkspaceItem[] }) => {
          return quizzes.every((q) => q.type === 'quiz');
        },
        showProBadge: !permissions.downloadQuiz,
        disabled: ({ data: quizzes }) =>
          quizzes.length > 1 || !canExportAnswerKey,
        paywallCallback: !permissions.downloadQuiz ? paywallCallback : null,
      } as Action<WorkspaceItem>),
    [canExportAnswerKey, permissions.downloadQuiz, paywallCallback]
  );

  return {
    exportAnswerKeyAction,
    isAnswerKeyDialogOpen,
    setIsAnswerKeyDialogOpen,
    selectedQuizId,
    selectedWorkspaceId,
  };
}
